import TextArea from 'antd/lib/input/TextArea';
import React, { Dispatch, FC, SetStateAction, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { useTranslate } from 'hooks/use-translation';
import { NON_PROJECT_ACTIVITY_ID } from '../../single-user-view-table/action-btn-cell';
import { TimesheetWeeklyRow, TimesheetWeeklyRowTask } from '../../types';
import styles from './reject-note-form.module.css';

const TextareaStyled = styled(TextArea)`
  min-height: 128px;
  padding: var(--spacing-04) 20% 0 var(--spacing-04);
`;

const StyledForm = styled.form`
  width: 360px;
`;

const StyledRejectTaskWrapper = styled.div`
  background-color: var(--white-100);
  padding: 0 var(--spacing-03);
`;

const StyledFloatingFormWrapper = styled.div`
  background-color: var(--white-100);
  padding-top: var(--spacing-04);
  padding-bottom: var(--spacing-04);
`;

interface Props {
  placeholder?: string;
  onChange?: (value: string) => void;
  closePopup: () => void;
}

interface RejectTimesheet extends Props {
  onReject: (content?: string, projectId?: number) => Promise<any>;
  projectId?: number;
}

interface RejectTimesheetTask extends Props {
  onReject: (
    task: TimesheetWeeklyRowTask,
    content?: string,
    projectId?: number,
  ) => Promise<any>;
  record: TimesheetWeeklyRow;
}

const BaseRejectNoteForm: FC<
  Props & {
    onFormSubmit: () => void;
    commentContent: string;
    setContent: Dispatch<SetStateAction<string>>;
    isSubmitting: boolean;
  }
> = (props) => {
  const translate = useTranslate();
  const {
    closePopup,
    onChange,
    placeholder = translate('timesheet.approval.reject.placeholder'),
    onFormSubmit,
    commentContent,
    setContent,
    isSubmitting,
  } = props;

  return (
    <>
      <StyledForm
        className={`${styles.form} ${styles['form--ready']}`}
        onSubmit={onFormSubmit}
      >
        <div className={styles.editor__wrapper}>
          <div className={`${styles.rte__wrapper} form__editor`}>
            <TextareaStyled
              value={commentContent}
              placeholder={placeholder}
              onChange={(e) => {
                setContent(e.target.value);
                onChange?.(e.target.value);
              }}
              onPressEnter={onFormSubmit}
            />
          </div>
        </div>
        <div className={styles.actions}>
          <Button
            size="small"
            type="link"
            onClick={() => {
              setContent('');
              closePopup();
            }}
          >
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            size="small"
            onClick={onFormSubmit}
            loading={isSubmitting}
            type="primary"
            danger
          >
            <FormattedMessage id="timesheets.feature.reject" />
          </Button>
        </div>
      </StyledForm>
    </>
  );
};

export const RejectNoteForm: FC<RejectTimesheet> = (props) => {
  const [commentContent, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onFormSubmit = () => {
    setIsSubmitting(true);
    props
      .onReject(commentContent, props.projectId)
      .then(() => {
        setContent('');
        props.closePopup();
      })
      .catch(() => {
        // toast to indicate on failure
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <StyledFloatingFormWrapper>
      <BaseRejectNoteForm
        {...props}
        onFormSubmit={onFormSubmit}
        isSubmitting={isSubmitting}
        commentContent={commentContent}
        setContent={setContent}
      />
    </StyledFloatingFormWrapper>
  );
};

export const RejectTaskNoteForm: FC<RejectTimesheetTask> = (props) => {
  const [commentContent, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onFormSubmit = () => {
    setIsSubmitting(true);
    props
      .onReject(
        props.record.task,
        commentContent,
        props.record.task.project?.id ?? NON_PROJECT_ACTIVITY_ID,
      )
      .then(() => {
        setContent('');
        props.closePopup();
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <StyledRejectTaskWrapper>
      <BaseRejectNoteForm
        {...props}
        onFormSubmit={onFormSubmit}
        isSubmitting={isSubmitting}
        commentContent={commentContent}
        setContent={setContent}
      />
    </StyledRejectTaskWrapper>
  );
};
